import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Send, MessageCircle, Bomb, Users, ArrowLeft, Plus, Maximize2, Image, Search, X, Loader2, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { useLeagueChat } from '@/hooks/useLeagueChat';
import { mockLeagueMessages, mockConversations, mockDirectMessages, leagueMembers as mockMembers } from '@/data/chatData';
import ChatModal from '@/components/chat/ChatModal';

// Giphy API
const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || '';
const GIPHY_SEARCH_URL = 'https://api.giphy.com/v1/gifs/search';
const GIPHY_TRENDING_URL = 'https://api.giphy.com/v1/gifs/trending';

interface GiphyGif {
  id: string;
  title: string;
  images: {
    fixed_width_small: { url: string; width: string; height: string };
    fixed_width: { url: string; width: string; height: string };
  };
}

const isGifMessage = (msg: string) =>
  /^https?:\/\/.*\.giphy\.com\/.*\.(gif|webp)/i.test(msg.trim());

const InlineLeagueChat = () => {
  const chat = useLeagueChat();
  const useMock = !chat.hasLeague;
  const [newMessage, setNewMessage] = useState('');
  const [mockMsgs, setMockMsgs] = useState(mockLeagueMessages);
  const [mockDms, setMockDms] = useState(mockDirectMessages);
  const [activePartnerId, setActivePartnerId] = useState<string | null>(null);
  const [showNewDm, setShowNewDm] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const scrollEndRef = useRef<HTMLDivElement>(null);

  // GIF picker state
  const [gifOpen, setGifOpen] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [gifLoading, setGifLoading] = useState(false);
  const gifDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Fetch trending GIFs when picker opens
  useEffect(() => {
    if (!gifOpen) return;
    if (gifSearch.trim()) return;
    setGifLoading(true);
    fetch(`${GIPHY_TRENDING_URL}?api_key=${GIPHY_API_KEY}&limit=20&rating=pg-13`)
      .then(r => r.json())
      .then(data => setGifs(data.data || []))
      .catch(() => setGifs([]))
      .finally(() => setGifLoading(false));
  }, [gifOpen]);

  // Debounced GIF search
  useEffect(() => {
    if (!gifOpen) return;
    if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current);
    if (!gifSearch.trim()) {
      setGifLoading(true);
      fetch(`${GIPHY_TRENDING_URL}?api_key=${GIPHY_API_KEY}&limit=20&rating=pg-13`)
        .then(r => r.json())
        .then(data => setGifs(data.data || []))
        .catch(() => setGifs([]))
        .finally(() => setGifLoading(false));
      return;
    }
    gifDebounceRef.current = setTimeout(() => {
      setGifLoading(true);
      fetch(`${GIPHY_SEARCH_URL}?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(gifSearch)}&limit=20&rating=pg-13`)
        .then(r => r.json())
        .then(data => setGifs(data.data || []))
        .catch(() => setGifs([]))
        .finally(() => setGifLoading(false));
    }, 400);
    return () => { if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current); };
  }, [gifSearch, gifOpen]);

  const leagueMessages = useMock ? mockMsgs : chat.leagueMessages;

  useEffect(() => {
    const t = setTimeout(() => {
      // Scope scroll to the chat's own ScrollArea viewport — don't scroll the page
      const sentinel = scrollEndRef.current;
      if (!sentinel) return;
      const viewport = sentinel.closest('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }, 150);
    return () => clearTimeout(t);
  }, [leagueMessages, chat.activeDmMessages, activePartnerId]);

  useEffect(() => {
    if (activePartnerId && !useMock) {
      chat.loadDmThread(activePartnerId);
    }
  }, [activePartnerId, useMock]);

  const isOwn = (msg: any) => useMock ? msg.senderId === 't1' : (msg.user_id === chat.userId || msg.sender_id === chat.userId);

  const sendGifAsMessage = (gif: GiphyGif, sendFn: (msg: string) => void) => {
    const gifUrl = gif.images.fixed_width.url;
    sendFn(gifUrl);
    setGifOpen(false);
    setGifSearch('');
    setGifs([]);
  };

  const handleSendLeague = () => {
    if (!newMessage.trim()) return;
    if (useMock) {
      setMockMsgs(prev => [...prev, {
        id: `lm-${Date.now()}`, senderId: 't1', senderName: 'You',
        content: newMessage, timestamp: new Date(),
      }]);
    } else {
      chat.sendLeagueMessage(newMessage);
    }
    setNewMessage('');
  };

  const handleSendDM = () => {
    if (!newMessage.trim() || !activePartnerId) return;
    if (useMock) {
      const convId = mockConversations.find(c => c.participantId === activePartnerId)?.id;
      if (convId) {
        setMockDms(prev => ({
          ...prev,
          [convId]: [...(prev[convId] || []), {
            id: `dm-${Date.now()}`, senderId: 't1', senderName: 'You',
            recipientId: activePartnerId, recipientName: '',
            content: newMessage, timestamp: new Date(),
          }],
        }));
      }
    } else {
      chat.sendDirectMessage(activePartnerId, newMessage);
    }
    setNewMessage('');
  };

  const totalUnread = useMock
    ? mockConversations.reduce((s, c) => s + c.unreadCount, 0)
    : chat.totalUnread;

  const members = useMock
    ? mockMembers.filter(m => m.id !== 't1')
    : chat.leagueMembers;

  const conversationList = useMock
    ? mockConversations.map(c => ({
        partnerId: c.participantId, partnerName: c.participantName,
        lastMessage: c.lastMessage, lastTime: c.lastMessageTime, unread: c.unreadCount,
      }))
    : chat.dmConversations.map(c => ({
        partnerId: c.recipientId, partnerName: c.recipientName,
        lastMessage: c.lastMessage, lastTime: new Date(c.lastMessageTime), unread: c.unreadCount,
      }));

  const activeDmMsgs = useMock
    ? (mockDms[mockConversations.find(c => c.participantId === activePartnerId)?.id || ''] || [])
    : chat.activeDmMessages;

  const activePartnerName = useMock
    ? mockMembers.find(m => m.id === activePartnerId)?.name
    : chat.leagueMembers.find(m => m.id === activePartnerId)?.name
      || chat.dmConversations.find(c => c.recipientId === activePartnerId)?.recipientName;

  const renderMessage = (msg: any, own: boolean) => {
    const isSystem = msg.is_system || msg.isSystem;
    const isAdmin = !!msg.is_admin;
    const senderName = msg.sender_name || msg.senderName || '';
    const time = msg.created_at ? new Date(msg.created_at) : msg.timestamp || new Date();

    return (
      <div key={msg.id} className={cn("flex gap-2 mb-2.5", own && !isAdmin && "flex-row-reverse", isSystem && "justify-center")}>
        {isSystem ? (
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs">
            <Bomb className="w-3 h-3" />{msg.content}
          </div>
        ) : isAdmin ? (
          <div className="w-full flex gap-2 mb-0.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-orange-500 text-white">
              <Shield className="w-3.5 h-3.5" />
            </div>
            <div className="max-w-[80%] rounded-2xl px-3 py-1.5 bg-orange-500/15 border border-orange-500/30 rounded-bl-sm">
              <div className="flex items-center gap-1 mb-0.5">
                <p className="text-[10px] font-bold text-orange-400">{senderName}</p>
                <Badge className="bg-orange-500/20 text-orange-400 text-[8px] h-3.5 px-1">ADMIN</Badge>
              </div>
              {isGifMessage(msg.content) ? (
                <img src={msg.content.trim()} alt="GIF" className="rounded max-w-[200px] max-h-[150px]" loading="lazy" />
              ) : (
                <p className="text-sm leading-snug">{msg.content}</p>
              )}
              <p className="text-[9px] mt-0.5 text-muted-foreground">
                {formatDistanceToNow(time, { addSuffix: true })}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className={cn(
              "w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
              own ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            )}>{senderName.charAt(0)}</div>
            <div className={cn(
              "max-w-[75%] rounded-2xl px-3 py-1.5",
              own ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"
            )}>
              {!own && <p className="text-[10px] font-medium mb-0.5 opacity-70">{senderName}</p>}
              {isGifMessage(msg.content) ? (
                <img src={msg.content.trim()} alt="GIF" className="rounded max-w-[200px] max-h-[150px]" loading="lazy" />
              ) : (
                <p className="text-sm leading-snug">{msg.content}</p>
              )}
              <p className={cn("text-[9px] mt-0.5", own ? "text-primary-foreground/60" : "text-muted-foreground")}>
                {formatDistanceToNow(time, { addSuffix: true })}
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader className="pb-0 pt-4 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold font-heading flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-primary" />
              Chat
            </CardTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setModalOpen(true)} title="Open full chat">
              <Maximize2 className="w-3.5 h-3.5 text-muted-foreground" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 pt-2">
          <Tabs defaultValue="league" onValueChange={() => { setActivePartnerId(null); setShowNewDm(false); setNewMessage(''); }}>
            <div className="px-4">
              <TabsList className="w-full h-8">
                <TabsTrigger value="league" className="flex-1 text-xs gap-1.5 h-7">
                  <Users className="w-3.5 h-3.5" />League
                </TabsTrigger>
                <TabsTrigger value="dms" className="flex-1 text-xs gap-1.5 h-7">
                  <MessageCircle className="w-3.5 h-3.5" />DMs
                  {totalUnread > 0 && (
                    <Badge variant="destructive" className="h-4 w-4 p-0 flex items-center justify-center text-[9px]">{totalUnread}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* League Tab */}
            <TabsContent value="league" className="mt-0">
              <ScrollArea className="h-64 px-4">
                <div className="py-2">
                  {leagueMessages.length === 0 && (
                    <div className="text-center text-muted-foreground py-8">
                      <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No messages yet. Break the ice!</p>
                    </div>
                  )}
                  {leagueMessages.map(msg => renderMessage(msg, isOwn(msg)))}
                  <div ref={scrollEndRef} />
                </div>
              </ScrollArea>
              {/* GIF Picker */}
              {gifOpen && (
                <div className="border-t border-border bg-background/95 backdrop-blur-sm">
                  <div className="flex items-center gap-1.5 px-2 pt-2">
                    <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <Input value={gifSearch} onChange={(e) => setGifSearch(e.target.value)} placeholder="Search GIFs..." className="h-7 text-xs" autoFocus />
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => { setGifOpen(false); setGifSearch(''); setGifs([]); }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="h-[160px] overflow-y-auto px-2 py-2">
                    {gifLoading ? (
                      <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                    ) : gifs.length === 0 ? (
                      <p className="text-center text-muted-foreground text-xs py-8">{gifSearch.trim() ? 'No GIFs found' : 'Loading...'}</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-1.5">
                        {gifs.map((gif) => (
                          <button key={gif.id} onClick={() => sendGifAsMessage(gif, useMock ? (m) => setMockMsgs(prev => [...prev, { id: `lm-${Date.now()}`, senderId: 't1', senderName: 'You', content: m, timestamp: new Date() }]) : chat.sendLeagueMessage)} className="rounded overflow-hidden hover:ring-2 ring-primary transition-all cursor-pointer">
                            <img src={gif.images.fixed_width_small.url} alt={gif.title} className="w-full h-auto" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="px-2 pb-1.5">
                    <img src="https://media.giphy.com/attribution/powered-by-giphy-dark.png" alt="Powered by GIPHY" className="h-3 opacity-50" />
                  </div>
                </div>
              )}
              <div className="p-3 border-t border-border">
                <form onSubmit={(e) => { e.preventDefault(); handleSendLeague(); }} className="flex gap-2">
                  <Button type="button" size="icon" variant={gifOpen ? 'secondary' : 'ghost'} className={cn("h-9 w-9 shrink-0", gifOpen && "bg-primary/20")} onClick={() => setGifOpen(!gifOpen)}>
                    <Image className="w-3.5 h-3.5" />
                  </Button>
                  <Input placeholder="Message the league..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} className="flex-1 h-9 text-sm" />
                  <Button type="submit" size="icon" className="h-9 w-9" disabled={!newMessage.trim()}><Send className="w-3.5 h-3.5" /></Button>
                </form>
              </div>
            </TabsContent>

            {/* DMs Tab */}
            <TabsContent value="dms" className="mt-0">
              {!activePartnerId && !showNewDm ? (
                <>
                  <div className="px-4 py-2">
                    <Button variant="outline" size="sm" className="w-full text-xs h-8" onClick={() => setShowNewDm(true)}>
                      <Plus className="w-3.5 h-3.5 mr-1.5" />New Message
                    </Button>
                  </div>
                  <ScrollArea className="h-56 px-4">
                    <div className="space-y-0.5 pb-2">
                      {conversationList.map(conv => (
                        <button key={conv.partnerId} onClick={() => setActivePartnerId(conv.partnerId)}
                          className="w-full flex items-center gap-2.5 p-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">{conv.partnerName.charAt(0)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">{conv.partnerName}</p>
                              <span className="text-[10px] text-muted-foreground">{formatDistanceToNow(conv.lastTime, { addSuffix: true })}</span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{conv.lastMessage || 'No messages yet'}</p>
                          </div>
                          {conv.unread > 0 && (
                            <Badge variant="destructive" className="h-4 w-4 p-0 flex items-center justify-center text-[9px]">{conv.unread}</Badge>
                          )}
                        </button>
                      ))}
                      {conversationList.length === 0 && (
                        <div className="text-center text-muted-foreground py-6">
                          <p className="text-xs">No conversations yet</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </>
              ) : showNewDm ? (
                <>
                  <div className="px-4 py-2 flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowNewDm(false)}><ArrowLeft className="w-3.5 h-3.5" /></Button>
                    <span className="text-sm font-medium">New Message</span>
                  </div>
                  <Separator />
                  <ScrollArea className="h-56 px-4">
                    <div className="space-y-0.5 py-2">
                      {members.map(member => (
                        <button key={member.id} onClick={() => { setActivePartnerId(member.id); setShowNewDm(false); }}
                          className="w-full flex items-center gap-2.5 p-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">{member.name.charAt(0)}</div>
                          <span className="text-sm font-medium">{member.name}</span>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </>
              ) : (
                <>
                  <div className="px-4 py-2 flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setActivePartnerId(null)}><ArrowLeft className="w-3.5 h-3.5" /></Button>
                    <span className="text-sm font-medium">{activePartnerName}</span>
                  </div>
                  <Separator />
                  <ScrollArea className="h-48 px-4">
                    <div className="py-2">
                      {activeDmMsgs.length === 0 ? (
                        <div className="text-center text-muted-foreground py-6">
                          <MessageCircle className="w-8 h-8 mx-auto mb-1.5 opacity-40" />
                          <p className="text-xs">Start the conversation!</p>
                        </div>
                      ) : (
                        activeDmMsgs.map(msg => renderMessage(msg, isOwn(msg)))
                      )}
                      <div ref={scrollEndRef} />
                    </div>
                  </ScrollArea>
                  {/* GIF Picker for DMs */}
                  {gifOpen && (
                    <div className="border-t border-border bg-background/95 backdrop-blur-sm">
                      <div className="flex items-center gap-1.5 px-2 pt-2">
                        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <Input value={gifSearch} onChange={(e) => setGifSearch(e.target.value)} placeholder="Search GIFs..." className="h-7 text-xs" autoFocus />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => { setGifOpen(false); setGifSearch(''); setGifs([]); }}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="h-[160px] overflow-y-auto px-2 py-2">
                        {gifLoading ? (
                          <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                        ) : gifs.length === 0 ? (
                          <p className="text-center text-muted-foreground text-xs py-8">{gifSearch.trim() ? 'No GIFs found' : 'Loading...'}</p>
                        ) : (
                          <div className="grid grid-cols-3 gap-1.5">
                            {gifs.map((gif) => (
                              <button key={gif.id} onClick={() => { if (activePartnerId) sendGifAsMessage(gif, (m) => useMock ? (() => {})() : chat.sendDirectMessage(activePartnerId, m)); }} className="rounded overflow-hidden hover:ring-2 ring-primary transition-all cursor-pointer">
                                <img src={gif.images.fixed_width_small.url} alt={gif.title} className="w-full h-auto" loading="lazy" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="px-2 pb-1.5">
                        <img src="https://media.giphy.com/attribution/powered-by-giphy-dark.png" alt="Powered by GIPHY" className="h-3 opacity-50" />
                      </div>
                    </div>
                  )}
                  <div className="p-3 border-t border-border">
                    <form onSubmit={(e) => { e.preventDefault(); handleSendDM(); }} className="flex gap-2">
                      <Button type="button" size="icon" variant={gifOpen ? 'secondary' : 'ghost'} className={cn("h-9 w-9 shrink-0", gifOpen && "bg-primary/20")} onClick={() => setGifOpen(!gifOpen)}>
                        <Image className="w-3.5 h-3.5" />
                      </Button>
                      <Input placeholder="Send a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} className="flex-1 h-9 text-sm" />
                      <Button type="submit" size="icon" className="h-9 w-9" disabled={!newMessage.trim()}><Send className="w-3.5 h-3.5" /></Button>
                    </form>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <ChatModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
};

export default InlineLeagueChat;
